import { getUserData } from '@/components/marketing-landing-page/utils';
import { MoengageUserAttributes } from '@/utils/enums';
import {
  getLocalStorageValue,
  getUserTypeFromPaidBatch,
  isEnvProduction,
  isFromApp,
} from '@/utils/utils';
import { EVENT_PLATFORMS, EVENTS_ORG, LoginMethods, User } from '@pw-tech/web-sdk';

export const getUserParams = (forMoengage: boolean = false) => {
  const userDataString = typeof window !== 'undefined' ? localStorage.getItem('user') : undefined;
  const { paid_batch_type } = getPaidParams({
    is_paid_batch_type_required: true,
    is_user_status_required: true,
  });
  if (!userDataString) {
    return {
      user_id: '',
      user_class: '',
      user_name: '',
      pw_user_id: '',
      user_cohort: '',
    };
  }

  const userData = JSON.parse(userDataString);

  const profile_id = userData?.profileId?._id;
  const user_id = userData?.id;
  let user_name = userData?.firstName;

  if (userData?.lastName) {
    user_name += ' ' + userData?.lastName;
  }

  const user_class = userData?.profileId?.class;
  const user_cohort = userData?.profileId?.cohortId;

  if (forMoengage) {
    return {
      user_id,
      user_class,
      user_name,
      user_cohort,
      selected_cohort: user_cohort,
      user_country: userData?.countryGroup,
      country_code: userData?.countryCode,
      user_created_at: userData?.createdAt,
      mobile_number: userData?.primaryNumber,
      user_type: getUserTypeFromPaidBatch(paid_batch_type) || '',
      user_grade: userData?.profileId?.class || '',
      language_preferred: userData?.profileId?.language || '',
      flow: 'revamped',
      URL: typeof window !== 'undefined' ? window.location.href : '',
      moe_logged_in_status: !!user_id,
    };
  } else {
    return {
      user_id: profile_id,
      pw_user_id: user_id,
      user_cohort,
      user_class,
      user_name,
    };
  }
};

export const getCampaignParams = (forMoengage: boolean = false) => {
  const searchParams = new URLSearchParams(
    typeof window !== 'undefined' ? window.location.search : ''
  );
  const utmParamKeys = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'gclid',
    'fbclid',
    'igshid',
  ];

  let areUtmParamsPresentInUrl = false;

  const utmParams: {
    [value: string]: string;
  } = {};

  searchParams.forEach((value, key) => {
    if (utmParamKeys.includes(key)) {
      areUtmParamsPresentInUrl = true;
      utmParams[key] = value;
    }
  });

  // utm_source=streaks overrides only the source; other UTMs stay from the stored campaign.
  const isFromStreaks = utmParams.utm_source === 'streaks';

  if (areUtmParamsPresentInUrl && !isFromStreaks) {
    if (forMoengage) {
      return {
        utm_source: utmParams.utm_source || '',
        utm_medium: utmParams.utm_medium || '',
        utm_campaign: utmParams.utm_campaign || '',
      };
    } else {
      return {
        utm_source: utmParams.utm_source,
        utm_medium: utmParams.utm_medium,
        utm_campaign: utmParams.utm_campaign,
        utm_term: utmParams.utm_term,
        utm_content: utmParams.utm_content,
        gclid: utmParams.gclid,
        fbclid: utmParams.fbclid,
        igshid: utmParams.igshid,
      };
    }
  } else {
    const storedUtmParams =
      typeof window !== 'undefined' ? localStorage.getItem('utmParams') : undefined;
    const stored = JSON.parse(storedUtmParams || '{}');
    const { utm_medium, utm_campaign, utm_term, utm_content, gclid, fbclid, igshid } = stored;
    const utm_source = isFromStreaks ? 'streaks' : stored.utm_source;

    if (forMoengage) {
      return {
        utm_source: utm_source || '',
        utm_medium: utm_medium || '',
        utm_campaign: utm_campaign || '',
      };
    } else {
      return {
        utm_source,
        utm_medium,
        utm_campaign,
        utm_term,
        utm_content,
        gclid,
        fbclid,
        igshid,
      };
    }
  }
};

export const getSource = () => {
  if (!isFromApp()) {
    return 'web';
  }

  return 'webapp';
};

export const getPaidParams = ({
  is_paid_batch_type_required,
  is_user_status_required,
}: {
  is_paid_batch_type_required: boolean;
  is_user_status_required: boolean;
}) => {
  const paidDataString =
    typeof window !== 'undefined' ? localStorage.getItem('paidData') : undefined;

  const { user_status, paid_batch_type } = JSON.parse(paidDataString || '{}');

  if (is_user_status_required && is_paid_batch_type_required) {
    return {
      user_status: user_status || '',
      paid_batch_type: paid_batch_type || '',
    };
  }

  if (is_user_status_required) {
    return {
      user_status: user_status || '',
    };
  }

  if (is_paid_batch_type_required) {
    return {
      paid_batch_type: paid_batch_type || '',
    };
  }

  return {};
};

export const trackGaEvent = ({
  eventName,
  eventParams,
}: {
  eventName: string;
  eventParams?: any;
}) => {
  const userData = getUserParams();

  const utmParams = getCampaignParams();

  const source = eventParams?.source || getSource();
  let paidParams = {};
  if (eventParams?.is_paid_batch_type_required || eventParams?.is_user_status_required) {
    paidParams = getPaidParams({
      is_user_status_required: eventParams?.is_user_status_required,
      is_paid_batch_type_required: eventParams?.is_paid_batch_type_required,
    });
    delete eventParams.is_paid_batch_type_required;
    delete eventParams.is_user_status_required;
  }

  const updatedData = {
    ...userData,
    ...utmParams,
    ...eventParams,
    source,
    ...paidParams,
  };

  // Enhanced tracking with multiple fallbacks for Google Tag Assistant detection
  try {
    // Primary: Use PWWebSDK if available
    if (window?.PWWebSDK?.eventService?.logEvent) {
      window.PWWebSDK.eventService.logEvent({
        eventName,
        eventData: updatedData,
        logTo: ['ga'],
      });
    } else {
      // Fallback 1: Direct gtag call for Google Tag Assistant
      if (typeof window !== 'undefined' && (window as any).gtag) {
        (window as any).gtag('event', eventName, updatedData);
      } else if (typeof window !== 'undefined' && (window as any).dataLayer) {
        (window as any).dataLayer.push({
          event: eventName,
          ...updatedData,
        });
      }
    }

    // Debug logging for development
    if (process.env.NODE_ENV === 'development') {
      console.log('📊 GA Event Tracked:', {
        event: eventName,
        data: updatedData,
        method: window?.PWWebSDK?.eventService?.logEvent ? 'PWWebSDK' : 'Direct gtag/dataLayer',
      });
    }
  } catch (error) {
    console.warn('GA tracking error (handled):', error);
  }
};

export const trackMoeEvent = ({
  eventName,
  eventParams,
}: {
  eventName: string;
  eventParams?: any;
}) => {
  const userData = getUserParams(true);

  if (!userData?.user_id) return;

  try {
    // fallback to login if Moengage userId is not set from the webSDK
    if (typeof window !== 'undefined' && !(window as any)?.Moengage?.getUserId?.()) {
      window?.PWWebSDK?.eventService?.userLogin?.();
    }
  } catch (error) {
    console.error('Error during Moengage user login:', error);
  }

  if (eventParams?.delete_selected_cohort) {
    delete userData.selected_cohort;
    delete eventParams.delete_selected_cohort;
  }

  const utmParams = getCampaignParams(true);

  const source = eventParams?.source || getSource();
  let paidParams = {};
  if (eventParams?.is_paid_batch_type_required || eventParams?.is_user_status_required) {
    paidParams = getPaidParams({
      is_user_status_required: eventParams?.is_user_status_required,
      is_paid_batch_type_required: eventParams?.is_paid_batch_type_required,
    });
    delete eventParams.is_paid_batch_type_required;
    delete eventParams.is_user_status_required;
  }
  const updatedData = {
    ...userData,
    ...utmParams,
    ...eventParams,
    source,
    ...paidParams,
  };

  try {
    // Try PWWebSDK first
    if (window?.PWWebSDK?.eventService?.logEvent) {
      window.PWWebSDK.eventService.logEvent({
        eventName,
        eventData: updatedData,
        logTo: ['moengage'],
      });
      console.log('✅ MoEngage event via PWWebSDK:', eventName);
    } else {
      // Fallback to direct MoEngage
      const moengage = (window as any)?.Moengage;
      if (moengage && typeof moengage.track_event === 'function') {
        // Set user attributes if available
        if (updatedData.user_id && typeof moengage.add_unique_user_id === 'function') {
          moengage.add_unique_user_id(updatedData.user_id);
        }
        if (updatedData.user_name && typeof moengage.add_user_name === 'function') {
          moengage.add_user_name(updatedData.user_name);
        }
        if (updatedData.mobile_number && typeof moengage.add_mobile === 'function') {
          moengage.add_mobile(updatedData.mobile_number);
        }

        // Track the event
        moengage.track_event(eventName, updatedData);
        console.log('✅ MoEngage event via direct API:', eventName);
      } else {
        console.warn('Neither PWWebSDK nor MoEngage available for event:', eventName);
      }
    }
  } catch (error) {
    console.warn('MoEngage tracking error (handled):', error);

    // Last resort: try direct MoEngage call
    try {
      const moengage = (window as any)?.Moengage;
      if (moengage?.track_event) {
        moengage.track_event(eventName, updatedData);
        console.log('✅ MoEngage event via fallback:', eventName);
      }
    } catch (fallbackError) {
      console.warn('MoEngage fallback failed:', fallbackError);
    }
  }
};

export const manualMoengageLogin = ({ user }: { user: User }) => {
  (window as any)?.Moengage?.add_unique_user_id?.(user?.id);
  (window as any)?.Moengage?.add_first_name?.(user.firstName);
  (window as any)?.Moengage?.add_last_name?.(user.lastName);
  (window as any)?.Moengage?.add_mobile?.(user?.primaryNumber);
  (window as any)?.Moengage?.add_user_name?.(user?.username);
};

export const initMoengage = () => {
  try {
    window?.PWWebSDK?.updateConfig({
      organizationId: process.env.NEXT_PUBLIC_CURIOUSJR_ORG_ID || '',
      apiBaseUrl: process.env.NEXT_PUBLIC_API_BASE_URL_AUTH || '',
      loginMethods: [LoginMethods.OTP],
      localStorageFallback: true,
      clientId: 'system-admin',
      clientSecret: 'KjPXuAVfC5xbmgreETNMaL7z',
      contextIdentifier: 'TOKEN_CONTEXT',
      loginPageUrl: '/',
      debugMode: !isEnvProduction(),
      eventConfig: {
        organizationName: EVENTS_ORG.CURIOUS_JR,
        eventPlatforms: [EVENT_PLATFORMS.GA, EVENT_PLATFORMS.MOENGAGE],
        eventsDebugMode: !isEnvProduction(),
      },
    });

    const userData = getUserData() || {};
    const { paid_batch_type } = getPaidParams({
      is_paid_batch_type_required: true,
      is_user_status_required: true,
    });

    window?.PWWebSDK?.eventService?.userLogin();
    (window as any)?.Moengage.add_user_attribute(
      MoengageUserAttributes.SELECTED_COHORT,
      userData?.profileId?.cohortId || ''
    );
    (window as any)?.Moengage.add_user_attribute(
      MoengageUserAttributes.USER_TYPE,
      getUserTypeFromPaidBatch(paid_batch_type) || ''
    );
    (window as any)?.Moengage.add_user_attribute(
      MoengageUserAttributes.USER_GRADE,
      userData?.profileId?.class || ''
    );
    (window as any)?.Moengage.add_user_attribute(
      MoengageUserAttributes.PREFERRED_LANGUAGE,
      userData?.profileId?.language || ''
    );
  } catch (error) {
    // login into Moengage directly if PWWebSDK fails
    const user = getLocalStorageValue('user', {});
    manualMoengageLogin(user);
  }
};
